import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../../App";
import { useVoice } from "../../contexts/VoiceContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import DialerKey from "./DialerKey";
import {
  Phone, PhoneOff, PhoneIncoming, PhoneOutgoing, Mic, MicOff,
  Search, User, Wrench, Clock, X, ChevronDown, Delete,
  Minimize2, AlertTriangle, Pause,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const formatDuration = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const formatPhone = (num) => {
  if (!num) return "";
  const cleaned = num.replace(/\D/g, "");
  if (cleaned.length === 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  if (cleaned.length === 11) return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  return num;
};

/* ── Number Pad ── */
const DialPad = ({ onDigit, onCall, onDelete, phoneNumber, disabled }) => {
  const keys = [
    ["1", ""], ["2", "ABC"], ["3", "DEF"],
    ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
    ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
    ["*", ""], ["0", "+"], ["#", ""],
  ];

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="grid grid-cols-3 gap-2 w-full max-w-[260px]">
        {keys.map(([digit, letters]) => (
          <DialerKey
            key={digit}
            onPress={() => onDigit(digit)}
            // mode="digit" is the default — fires on pointerdown so the
            // press feels native on iOS.
            className="h-14 rounded-xl bg-muted/50 hover:bg-muted border border-border/40 flex flex-col items-center justify-center"
            // press visual is handled by DialerKey (transform + state) so we
            // don't add `active:scale-95` here — that would conflict with
            // our pointerdown-driven press effect on iOS.
            data-testid={`dial-key-${digit}`}
          >
            <span className="text-xl font-semibold text-foreground leading-none">{digit}</span>
            {letters && <span className="text-[9px] text-muted-foreground tracking-[0.2em] mt-0.5">{letters}</span>}
          </DialerKey>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-1">
        <DialerKey
          onPress={onCall}
          mode="action"
          disabled={disabled || !phoneNumber}
          className="h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/30 flex items-center justify-center disabled:opacity-50"
          data-testid="dial-call-btn"
        >
          <Phone className="w-6 h-6" />
        </DialerKey>
        {phoneNumber && (
          <DialerKey
            onPress={onDelete}
            // delete fires on press so backspacing feels instant
            className="h-10 w-10 rounded-full text-muted-foreground hover:text-foreground flex items-center justify-center"
            data-testid="dial-delete-btn"
          >
            <Delete className="w-5 h-5" />
          </DialerKey>
        )}
      </div>
    </div>
  );
};

/* ── Active Call UI ── */
const ActiveCallView = ({ toNumber, contactName, duration, isMuted, onHangUp, onToggleMute, onSendDigit, callState, onHold }) => {
  const [showDtmf, setShowDtmf] = useState(false);

  return (
    <div className="flex flex-col items-center gap-4 py-6" data-testid="active-call-view">
      <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
        <Phone className="w-8 h-8 text-emerald-500" />
      </div>
      <div className="text-center">
        {contactName && <p className="text-lg font-semibold text-foreground">{contactName}</p>}
        <p className="text-muted-foreground text-sm">{formatPhone(toNumber)}</p>
        <p className="text-foreground font-mono text-2xl mt-2" data-testid="call-timer">
          {callState === "connected" ? formatDuration(duration) : callState === "ringing" ? "Ringing..." : "Connecting..."}
        </p>
      </div>

      {showDtmf && (
        <div className="grid grid-cols-3 gap-1.5 w-full max-w-[200px]">
          {["1","2","3","4","5","6","7","8","9","*","0","#"].map(d => (
            <DialerKey
              key={d}
              onPress={() => onSendDigit(d)}
              className="h-10 rounded-lg bg-muted/50 hover:bg-muted text-foreground font-semibold text-lg flex items-center justify-center"
              data-testid={`dtmf-${d}`}
            >
              {d}
            </DialerKey>
          ))}
        </div>
      )}

      <div className="flex items-center gap-4 mt-2">
        <DialerKey
          onPress={onToggleMute}
          // mute is fast-acting, fire on press so the user sees the change
          className={`h-12 w-12 rounded-full flex items-center justify-center ${isMuted ? "bg-red-500/20 text-red-500" : "bg-muted text-muted-foreground hover:text-foreground"}`}
          data-testid="call-mute-btn"
        >
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </DialerKey>
        <DialerKey
          onPress={() => setShowDtmf(!showDtmf)}
          className="h-12 w-12 rounded-full bg-muted text-muted-foreground hover:text-foreground text-lg font-bold flex items-center justify-center"
          data-testid="call-keypad-btn"
        >
          #
        </DialerKey>
        <DialerKey
          onPress={onHold}
          disabled={callState !== "connected"}
          className="h-12 w-12 rounded-full bg-amber-500/15 text-amber-500 hover:bg-amber-500/25 flex items-center justify-center disabled:opacity-40"
          data-testid="call-hold-btn"
        >
          <Pause className="w-5 h-5" />
        </DialerKey>
        <DialerKey
          onPress={onHangUp}
          mode="action"
          className="h-14 w-14 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30 flex items-center justify-center"
          data-testid="call-hangup-btn"
        >
          <PhoneOff className="w-6 h-6" />
        </DialerKey>
      </div>
    </div>
  );
};

/* ── Held Calls ── */
const HeldCallsList = ({ heldCalls, onResume, resumeDisabled }) => {
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!heldCalls.length) return undefined;
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [heldCalls.length]);

  if (!heldCalls.length) return null;
  return (
    <div className="space-y-1.5 mb-3" data-testid="held-calls-list">
      {heldCalls.map((h) => {
        const secs = h.held_at ? Math.max(0, Math.floor((Date.now() - new Date(h.held_at).getTime()) / 1000)) : 0;
        return (
          <div key={h.pstn_sid} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25" data-testid={`held-call-${h.pstn_sid}`}>
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
              <Pause className="w-4 h-4 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{formatPhone(h.caller_number)}</p>
              <p className="text-[11px] text-amber-600 dark:text-amber-400">On hold · {formatDuration(secs)}</p>
            </div>
            <Button
              size="sm"
              onClick={() => onResume(h.pstn_sid)}
              disabled={resumeDisabled}
              className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid={`held-resume-${h.pstn_sid}`}
            >
              <Phone className="w-3 h-3 mr-1" /> Resume
            </Button>
          </div>
        );
      })}
    </div>
  );
};

/* ── Contact List ── */
const ContactList = ({ onSelect }) => {
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/voice/contacts?q=${encodeURIComponent(q)}`, getAuthConfig());
      setContacts(res.data);
    } catch { setContacts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { search(""); }, [search]);

  const handleSearch = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  return (
    <div className="flex flex-col gap-2 h-full" data-testid="contacts-tab">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search contacts..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-9 bg-muted/50 border-border/40 h-9 text-sm"
          data-testid="contacts-search"
        />
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y space-y-0.5 max-h-[280px] -mx-1 px-1">
        {loading ? (
          <p className="text-center text-muted-foreground text-sm py-4">Searching...</p>
        ) : contacts.length === 0 ? (
          <p className="text-center text-muted-foreground text-sm py-4">No contacts with phone numbers</p>
        ) : (
          contacts.map(c => (
            <button
              key={c.id}
              onClick={() => onSelect(c)}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
              data-testid={`contact-${c.id}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${c.type === "customer" ? "bg-blue-500" : "bg-amber-500"}`}>
                {c.type === "customer" ? <User className="w-4 h-4" /> : <Wrench className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground">{formatPhone(c.phone)}</p>
              </div>
              <Badge className={`text-[10px] ${c.type === "customer" ? "bg-blue-500/15 text-blue-500 border-blue-500/25" : "bg-amber-500/15 text-amber-500 border-amber-500/25"} border`}>
                {c.type}
              </Badge>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

/* ── Recent Calls ── */
const RecentCalls = ({ onCall }) => {
  const { timezone } = useTimezone();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/voice/calls?limit=30`, getAuthConfig());
        setCalls(res.data);
      } catch { setCalls([]); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  return (
    <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y space-y-0.5 max-h-[340px] -mx-1 px-1" data-testid="recent-calls-tab">
      {loading ? (
        <p className="text-center text-muted-foreground text-sm py-4">Loading...</p>
      ) : calls.length === 0 ? (
        <p className="text-center text-muted-foreground text-sm py-4">No recent calls</p>
      ) : (
        calls.map((call, i) => (
          <button
            key={i}
            onClick={() => onCall(call.direction === "outbound" ? call.to_number : call.from_number, { name: call.customer_name })}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
            data-testid={`recent-call-${i}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${call.direction === "outbound" ? "bg-blue-500/15 text-blue-500" : "bg-emerald-500/15 text-emerald-500"}`}>
              {call.direction === "outbound" ? <PhoneOutgoing className="w-4 h-4" /> : <PhoneIncoming className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {call.customer_name || formatPhone(call.direction === "outbound" ? call.to_number : call.from_number)}
              </p>
              <p className="text-xs text-muted-foreground">
                {call.created_at ? new Date(call.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: timezone }) : ""}
                {call.duration > 0 && ` \u2022 ${formatDuration(call.duration)}`}
              </p>
            </div>
            <Phone className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100" />
          </button>
        ))
      )}
    </div>
  );
};


/* ══════════════════════ MAIN DIALER PANEL ══════════════════════ */
const DialerPanel = ({ isOpen, onClose }) => {
  const {
    callState, isMuted, callDuration, callerId,
    minutesUsed, minutesLimit, isReady, micPermission,
    makeCall, hangUp, toggleMute, sendDigit, ensureRegistered,
    availableCallerIds, selectedCallerId, selectCallerId,
    heldCalls, holdCall, resumeCall,
  } = useVoice();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [tab, setTab] = useState("keypad"); // keypad | contacts | recent
  const [callToName, setCallToName] = useState("");
  const hasTriedRegister = useRef(false);

  // When panel opens, trigger device registration (user gesture context for Android)
  useEffect(() => {
    if (isOpen && !hasTriedRegister.current) {
      hasTriedRegister.current = true;
      ensureRegistered();
    }
  }, [isOpen, ensureRegistered]);

  const isInCall = callState !== "idle";

  const handleDigit = (d) => {
    if (isInCall) {
      sendDigit(d);
    } else {
      setPhoneNumber(prev => prev + d);
    }
  };

  const handleDelete = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  const handleCall = (number, contact) => {
    const num = number || phoneNumber;
    if (!num) return;
    setCallToName(contact?.name || "");
    makeCall(num, contact);
  };

  const handleContactSelect = (contact) => {
    setPhoneNumber(contact.phone);
    setCallToName(contact.name);
    setTab("keypad");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] transition-opacity" onClick={onClose} />

      {/* Panel */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[95] transform transition-transform duration-300 ease-out sm:left-auto sm:right-4 sm:bottom-4 sm:w-[380px] sm:rounded-2xl overflow-hidden"
        style={{ maxHeight: "90vh" }}
        data-testid="dialer-panel"
      >
        <div className="bg-background/95 backdrop-blur-xl border border-border/60 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: "85vh" }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-emerald-500" />
              <span className="font-semibold text-foreground text-sm">Phone</span>
              <div className={`w-1.5 h-1.5 rounded-full ${isReady ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`} />
            </div>
            <div className="flex items-center gap-2">
              {minutesLimit !== -1 && (
                <span className="text-[11px] text-muted-foreground" data-testid="minutes-display">
                  {Math.round(minutesUsed)}/{minutesLimit} min
                </span>
              )}
              <button onClick={onClose} className="p-1 rounded-md hover:bg-muted text-muted-foreground" data-testid="dialer-close">
                {isInCall ? <Minimize2 className="w-4 h-4" /> : <X className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden px-4 py-3">
            {/* Mic permission warning */}
            {micPermission === false && !isInCall && (
              <div className="flex items-center gap-2 p-2.5 mb-3 bg-orange-500/10 border border-orange-500/25 rounded-lg" data-testid="mic-warning">
                <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                <p className="text-xs text-orange-600 dark:text-orange-400">
                  Microphone blocked. Allow microphone access in your browser settings, then tap the call button to retry.
                </p>
              </div>
            )}

            {/* Calls waiting on hold */}
            <HeldCallsList heldCalls={heldCalls} onResume={resumeCall} resumeDisabled={isInCall} />

            {isInCall ? (
              <ActiveCallView
                toNumber={phoneNumber}
                contactName={callToName}
                duration={callDuration}
                isMuted={isMuted}
                onHangUp={hangUp}
                onToggleMute={toggleMute}
                onSendDigit={sendDigit}
                callState={callState}
                onHold={holdCall}
              />
            ) : (
              <>
                {/* Phone number display */}
                <div className="text-center mb-3">
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter number..."
                    className="text-center text-xl font-mono bg-transparent border-none focus:ring-0 h-10 text-foreground placeholder:text-muted-foreground/40"
                    data-testid="dialer-input"
                  />
                  {availableCallerIds.length > 1 ? (
                    <div className="flex items-center justify-center gap-1 mt-0.5">
                      <span className="text-[11px] text-muted-foreground shrink-0">From:</span>
                      <Select value={selectedCallerId} onValueChange={selectCallerId}>
                        <SelectTrigger
                          className="h-6 w-auto border-none bg-transparent px-1 py-0 gap-1 text-[11px] text-foreground shadow-none focus:ring-0"
                          data-testid="dialer-from-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[120]">
                          {availableCallerIds.map((n) => (
                            <SelectItem key={n.phone_number} value={n.phone_number} data-testid={`dialer-from-opt-${n.phone_number}`}>
                              {n.label ? `${n.label} — ` : ""}{formatPhone(n.phone_number)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (selectedCallerId || callerId) && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">From: {formatPhone(selectedCallerId || callerId)}</p>
                  )}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-3 bg-muted/30 rounded-lg p-0.5">
                  {[
                    { id: "keypad", label: "Keypad", icon: <span className="text-sm font-bold">#</span> },
                    { id: "contacts", label: "Contacts", icon: <User className="w-3.5 h-3.5" /> },
                    { id: "recent", label: "Recent", icon: <Clock className="w-3.5 h-3.5" /> },
                  ].map(t => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        tab === t.id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`tab-${t.id}`}
                    >
                      {t.icon}{t.label}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                {tab === "keypad" && (
                  <DialPad
                    onDigit={handleDigit}
                    onCall={() => handleCall()}
                    onDelete={handleDelete}
                    phoneNumber={phoneNumber}
                    disabled={!isReady}
                  />
                )}
                {tab === "contacts" && <ContactList onSelect={handleContactSelect} />}
                {tab === "recent" && <RecentCalls onCall={handleCall} />}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};


/* ── Incoming Call Notification ── */
export const IncomingCallBanner = () => {
  const { incomingCall, activeCall, acceptIncoming, rejectIncoming, holdAndAccept } = useVoice();

  if (!incomingCall) return null;

  const fromNumber = incomingCall.parameters?.From || "Unknown";
  const inCall = !!activeCall;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-top" data-testid="incoming-call-banner">
      <div className="bg-background/95 backdrop-blur-xl border border-emerald-500/40 rounded-2xl shadow-2xl shadow-emerald-500/20 p-4 flex items-center gap-4 min-w-[320px]">
        <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
          <PhoneIncoming className="w-6 h-6 text-emerald-500" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-foreground text-sm">Incoming Call</p>
          <p className="text-muted-foreground text-xs">{formatPhone(fromNumber)}</p>
          {inCall && (
            <p className="text-[10px] text-amber-500 mt-0.5" data-testid="incoming-while-in-call-note">
              You're on another call — answering puts it on hold
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <DialerKey
            onPress={rejectIncoming}
            mode="action"
            className="h-10 w-10 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center"
            data-testid="reject-call-btn"
          >
            <PhoneOff className="w-5 h-5" />
          </DialerKey>
          {inCall ? (
            <DialerKey
              onPress={holdAndAccept}
              mode="action"
              className="h-10 px-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-1"
              data-testid="hold-accept-call-btn"
            >
              <Pause className="w-4 h-4" />
              <Phone className="w-4 h-4" />
            </DialerKey>
          ) : (
            <DialerKey
              onPress={acceptIncoming}
              mode="action"
              className="h-10 w-10 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center"
              data-testid="accept-call-btn"
            >
              <Phone className="w-5 h-5" />
            </DialerKey>
          )}
        </div>
      </div>
    </div>
  );
};


/* ── Floating Action Button ── */
export const DialerFAB = ({ onClick }) => {
  const { callState, callDuration } = useVoice();
  const isInCall = callState !== "idle";
  const isNative = !!window.FieldzNative?.isNativeApp;

  return (
    <DialerKey
      onPress={onClick}
      mode="action"
      className={`fixed z-[80] h-14 rounded-full shadow-lg flex items-center gap-2 px-5 right-4 lg:bottom-6 lg:right-6 ${
        isNative ? "bottom-6" : "bottom-24"
      } ${
        isInCall
          ? "bg-emerald-500 text-white shadow-emerald-500/30 animate-pulse"
          : "bg-[#0046BE] text-white shadow-[#0046BE]/30 hover:bg-[#0046BE]/80"
      }`}
      data-testid="dialer-fab"
    >
      <Phone className="w-5 h-5" />
      {isInCall && <span className="font-mono text-sm">{formatDuration(callDuration)}</span>}
    </DialerKey>
  );
};

export default DialerPanel;
